export type DiffChange = 'unchanged' | 'added' | 'removed' | 'changed';

export interface DiffRow {
  left?: string;
  right?: string;
  leftNumber?: number;
  rightNumber?: number;
  change: DiffChange;
}

// Above this combined line count, skip the O(n*m) LCS and align positionally.
const LCS_LINE_LIMIT = 6000;

interface Op {
  type: 'eq' | 'del' | 'ins';
  left?: string;
  right?: string;
}

const computeOps = (a: string[], b: string[]): Op[] => {
  if (a.length + b.length > LCS_LINE_LIMIT) {
    return positionalOps(a, b);
  }

  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const ops: Op[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ type: 'eq', left: a[i], right: b[j] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ type: 'del', left: a[i] });
      i++;
    } else {
      ops.push({ type: 'ins', right: b[j] });
      j++;
    }
  }
  while (i < n) ops.push({ type: 'del', left: a[i++] });
  while (j < m) ops.push({ type: 'ins', right: b[j++] });
  return ops;
};

const positionalOps = (a: string[], b: string[]): Op[] => {
  const ops: Op[] = [];
  const max = Math.max(a.length, b.length);
  for (let k = 0; k < max; k++) {
    const left = a[k];
    const right = b[k];
    if (left !== undefined && right !== undefined) {
      if (left === right) {
        ops.push({ type: 'eq', left, right });
      } else {
        // del before ins: sideBySideDiff drains del runs first and pairs them positionally.
        ops.push({ type: 'del', left });
        ops.push({ type: 'ins', right });
      }
    } else if (left !== undefined) {
      ops.push({ type: 'del', left });
    } else if (right !== undefined) {
      ops.push({ type: 'ins', right });
    }
  }
  return ops;
};

export const sideBySideDiff = (leftText: string, rightText: string): DiffRow[] => {
  const a = leftText.split('\n');
  const b = rightText.split('\n');
  const ops = computeOps(a, b);

  const rows: DiffRow[] = [];
  let leftNo = 0;
  let rightNo = 0;

  let i = 0;
  while (i < ops.length) {
    const op = ops[i];

    if (op.type === 'eq') {
      leftNo++;
      rightNo++;
      rows.push({ left: op.left, right: op.right, leftNumber: leftNo, rightNumber: rightNo, change: 'unchanged' });
      i++;
      continue;
    }

    const dels: string[] = [];
    const inss: string[] = [];
    while (i < ops.length && ops[i].type === 'del') dels.push(ops[i++].left as string);
    while (i < ops.length && ops[i].type === 'ins') inss.push(ops[i++].right as string);

    const pairCount = Math.max(dels.length, inss.length);
    for (let k = 0; k < pairCount; k++) {
      const hasLeft = k < dels.length;
      const hasRight = k < inss.length;
      if (hasLeft) leftNo++;
      if (hasRight) rightNo++;
      rows.push({
        left: hasLeft ? dels[k] : undefined,
        right: hasRight ? inss[k] : undefined,
        leftNumber: hasLeft ? leftNo : undefined,
        rightNumber: hasRight ? rightNo : undefined,
        change: hasLeft && hasRight ? 'changed' : hasLeft ? 'removed' : 'added',
      });
    }
  }

  return rows;
};

export const countDifferences = (rows: DiffRow[]): number =>
  rows.filter((r) => r.change !== 'unchanged').length;
